import api, { USE_MOCK, qs } from './apiClient';
import mockApi from '../mock/backend';

export const sharingService = {
  listShares(documentId) {
    if (USE_MOCK) return mockApi.listShares(documentId);
    return api.get(`/documents/${documentId}/shares`);
  },

  share(documentId, payload) {
    if (USE_MOCK) return mockApi.createShare(documentId, payload);
    return api.post(`/documents/${documentId}/share`, payload);
  },

  updateShare(documentId, shareId, capabilities) {
    if (USE_MOCK) return mockApi.updateShare(shareId, capabilities);
    return api.patch(`/documents/${documentId}/shares/${shareId}`, { capabilities });
  },

  revokeShare(documentId, shareId) {
    if (USE_MOCK) return mockApi.revokeShare(shareId);
    return api.delete(`/documents/${documentId}/shares/${shareId}`);
  },

  listLinks(documentId) {
    if (USE_MOCK) return mockApi.listLinks(documentId);
    return api.get(`/documents/${documentId}/links`);
  },

  createLink(documentId, payload) {
    if (USE_MOCK) return mockApi.createLink(documentId, payload);
    return api.post(`/documents/${documentId}/links`, payload);
  },

  revokeLink(documentId, linkId) {
    if (USE_MOCK) return mockApi.revokeLink(linkId);
    return api.delete(`/documents/${documentId}/links/${linkId}`);
  },

  searchPrincipals(query) {
    if (USE_MOCK) return mockApi.searchPrincipals(query);
    return api.get(`/principals?q=${encodeURIComponent(query || '')}`);
  },

  /**
   * Public endpoint — no session required.
   *
   * `grant` is what a named recipient gets after proving they hold one of the addresses the
   * link was sent to. A link open to anyone never needs one; a restricted link answers
   * `email_required` until it has one.
   */
  resolvePublicLink(token, password, grant) {
    if (USE_MOCK) return mockApi.resolvePublicLink(token, password);
    return password === undefined
      ? api.get(`/public/links/${token}${qs({ grant })}`)
      : api.post(`/public/links/${token}/unlock`, { password, grant });
  },

  /** Ask for a one-time code. Answers the same way whether or not the address is on the list. */
  requestLinkCode(token, email) {
    if (USE_MOCK) return Promise.resolve({ sent: true });
    return api.post(`/public/links/${token}/verify`, { email });
  },

  /** Exchange the code for a grant. */
  confirmLinkCode(token, email, code) {
    if (USE_MOCK) return Promise.resolve({ state: 'invalid' });
    return api.post(`/public/links/${token}/verify/confirm`, { email, code });
  },

  /** A rendered page of a shared document, for a recipient with view access. */
  publicPreview(token, { page = 1, password, grant } = {}) {
    if (USE_MOCK) return Promise.resolve({ kind: 'none', page: 1, pageCount: null, url: null });
    return api.get(`/public/links/${token}/preview${qs({ page, password, grant })}`);
  },

  /** The signed URL behind the recipient's Download button. */
  publicDownload(token, { password, grant } = {}) {
    if (USE_MOCK) return Promise.resolve({ url: null });
    return api.get(`/public/links/${token}/download${qs({ password, grant })}`);
  },
};

export default sharingService;
